import { Box, Button, Flex, HStack, IconButton, Input, NumberInput, NumberInputField, Text } from '@chakra-ui/react';
import { FiPlus, FiTrash2 } from 'react-icons/fi';

import { useCampaignUI } from '../_ui/ui';
import { VariantEditor } from './StepMessage';

/**
 * The follow-ups a campaign sends itself, to whoever never replied.
 *
 * ★ EACH STEP IS A DIFFERENT MESSAGE, NOT A REPEAT. "Day 3" is usually a
 * shorter nudge than day 0, so a step carries its own template — which is why
 * it reuses the same variant editor as the first message rather than offering
 * a delay to apply to it.
 *
 * ★ THE DELAY IS MEASURED FROM THE PREVIOUS STEP FINISHING, not from the day
 * the campaign began. A first send spread over a week would otherwise have its
 * "three day" follow-up fire while it was still going out.
 *
 * ★ AND APPROVING THE CAMPAIGN APPROVES EVERY STEP. The screen says so,
 * because it is the one thing about a drip that is easy to get wrong in your
 * head: nobody is asked again on day seven, so the templates below are
 * reviewed now, with the first one.
 */

const MAX_STEPS = 10;

export default function DripSteps({ steps = [], templates = [], onChange }) {
	const ui = useCampaignUI();
	const list = Array.isArray(steps) ? steps : [];

	const set = (index, patch) => onChange(list.map((s, i) => (i === index ? { ...s, ...patch } : s)));

	const addStep = () =>
		onChange([
			...list,
			// Three days is the common first nudge, and a step with no variant
			// would be refused at approval — so it starts with one to fill in.
			{ afterDays: 3, name: '', variants: [{ id: 'a', weight: 1, paramMap: {} }] },
		]);

	return (
		<Box mt={8}>
			<Text fontSize='sm' color={ui.textPrimary} fontWeight='medium' mb={1}>
				Follow-ups (optional)
			</Text>
			<Text fontSize='xs' color={ui.textMuted} mb={3} maxW='78ch'>
				Sent only to people who received the message before it and never replied. Anyone who answers, or who opts
				out, drops out of the sequence. Approving this campaign approves every follow-up below — nobody is asked
				again when one is due.
			</Text>

			{!list.length && (
				<Text fontSize='xs' color={ui.textMuted} mb={2}>
					None. The campaign sends once and stops.
				</Text>
			)}

			{list.map((step, index) => {
				const variants = step.variants ?? [];
				const setVariants = (next) => set(index, { variants: next });
				return (
					// eslint-disable-next-line react/no-array-index-key
					<Box key={`step-${index}`} borderWidth='1px' borderColor={ui.border} borderRadius='md' p={4} mb={3}>
						<Flex justify='space-between' align='center' gap={3} mb={3} wrap='wrap'>
							<HStack spacing={2}>
								<Text fontSize='sm' color={ui.textPrimary} fontWeight='medium'>
									Follow-up {index + 1}
								</Text>
								<Text fontSize='xs' color={ui.textMuted}>
									send
								</Text>
								<NumberInput
									size='sm'
									maxW='80px'
									min={1}
									max={365}
									value={step.afterDays ?? 3}
									onChange={(value) => set(index, { afterDays: Number(value) || 1 })}
								>
									<NumberInputField bg={ui.inputBg} borderColor={ui.border} color={ui.textPrimary} />
								</NumberInput>
								<Text fontSize='xs' color={ui.textMuted}>
									days after {index === 0 ? 'the first message finishes' : `follow-up ${index} finishes`}
								</Text>
							</HStack>

							<HStack spacing={2}>
								<Input
									size='sm'
									maxW='190px'
									placeholder='Name (optional)'
									value={step.name ?? ''}
									onChange={(e) => set(index, { name: e.target.value })}
									bg={ui.inputBg}
									borderColor={ui.border}
									color={ui.textPrimary}
								/>
								<IconButton
									size='sm'
									variant='ghost'
									aria-label={`Remove follow-up ${index + 1}`}
									icon={<FiTrash2 />}
									onClick={() => onChange(list.filter((_, i) => i !== index))}
								/>
							</HStack>
						</Flex>

						{variants.map((variant, vIndex) => (
							<VariantEditor
								// eslint-disable-next-line react/no-array-index-key
								key={`step-${index}-v-${vIndex}`}
								variant={variant}
								templates={templates}
								canRemove={variants.length > 1}
								onChange={(next) => setVariants(variants.map((v, i) => (i === vIndex ? next : v)))}
								onRemove={() => setVariants(variants.filter((_, i) => i !== vIndex))}
							/>
						))}

						<Button
							size='xs'
							variant='neutralOutline'
							leftIcon={<FiPlus />}
							onClick={() =>
								setVariants([
									...variants,
									{ id: String.fromCharCode(97 + variants.length), weight: 1, paramMap: {} },
								])
							}
						>
							Add a variant to this follow-up
						</Button>
					</Box>
				);
			})}

			{list.length < MAX_STEPS && (
				<Button size='xs' variant='neutralOutline' leftIcon={<FiPlus />} onClick={addStep}>
					Add a follow-up
				</Button>
			)}
			{list.length >= MAX_STEPS && (
				<Text fontSize='xs' color={ui.textMuted}>
					{MAX_STEPS} follow-ups is the limit — beyond that the frequency cap would stop them anyway.
				</Text>
			)}
		</Box>
	);
}
